import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { z } from "zod";

import type { RevolutAccount, RevolutConnection, RevolutTransaction } from "@ravnar/api-contract";

import { useAskAgain } from "../../../api/refresh";
import {
	disconnectRevolut,
	revolutAccountsQueryOptions,
	revolutConnectionQueryOptions,
	revolutTransactionsQueryOptions,
	startRevolutConnection,
} from "../../../api/revolut";
import { PageHeader } from "../../../components/shell/app-shell";
import { Button } from "../../../components/ui/button";
import { Card, CardHeader } from "../../../components/ui/card";
import { ConfirmDialog } from "../../../components/ui/confirm-dialog";
import { Drawer } from "../../../components/ui/drawer";
import { Empty } from "../../../components/ui/empty";
import { Pill } from "../../../components/ui/pill";
import { Skeleton } from "../../../components/ui/skeleton";
import { Table, type TableColumn } from "../../../components/ui/table";
import { REVOLUT_SUBTYPE_LABELS, revolutPaidInBy } from "../../../domain/revolut";
import { formatAmount, formatShortDate } from "../../../lib/format";
import { openRevolutApproval } from "../../../lib/revolut-approval";
import { showToast } from "../../../lib/toast";
import { PaymentsCard } from "./-components/payments-card";

const revolutSearchSchema = z.object({
	/** The account whose latest transactions the drawer shows; none when it is closed. */
	account: z.string().optional().catch(undefined),
});

export const Route = createFileRoute("/settings/revolut/")({
	validateSearch: revolutSearchSchema,
	// Prefetch through the Query cache so the page can use useSuspenseQuery without a loading flash. The accounts are
	// read from Revolut, which takes a moment, so the page asks for them itself and shows them when they come.
	loader: ({ context }) => context.queryClient.query({ ...revolutConnectionQueryOptions(), staleTime: "static" }),
	head: () => ({ meta: [{ title: "Revolut · Ravnar" }] }),
	staticData: { crumbs: ["Settings", "Revolut"] },
	component: RevolutPage,
});

/** A connection an owner or admin made, which Revolut may or may not still honour. */
type MadeConnection = Extract<RevolutConnection, { status: "connected" | "reconnect" }>;

const HOW_IT_WORKS = [
	{
		title: "Connect Revolut",
		text: "Revolut opens and asks you to sign in to the business account Ravnar should read.",
	},
	{
		title: "Approve read-only access",
		text: "Revolut asks you to let Ravnar see your accounts, their balances and their transactions. Nothing you approve can move money.",
	},
	{
		title: "Come back to Ravnar",
		text: "Revolut sends you back here, where your accounts and their balances show, and Finance counts your cash from them.",
	},
] as const;

function RevolutPage() {
	const { data: connection } = useSuspenseQuery(revolutConnectionQueryOptions());
	return (
		<>
			<PageHeader
				title="Revolut"
				description="Your Revolut accounts and their balances in Ravnar. Ravnar reads them, and never moves money itself."
			/>
			{connection.status === "unavailable" ? (
				<Card>
					<Empty>Revolut is not set up for this deployment of Ravnar yet.</Empty>
				</Card>
			) : connection.status === "not-connected" ? (
				<div className="flex flex-col gap-3.5">
					<HowItWorks />
					<ConnectButton label="Connect Revolut" />
				</div>
			) : (
				<div className="flex flex-col gap-3.5">
					<ConnectedAccount connection={connection} />
					<PaymentsCard />
					{connection.status === "connected" ? <RevolutAccounts /> : null}
				</div>
			)}
		</>
	);
}

function HowItWorks() {
	return (
		<Card>
			<CardHeader title="How it works" />
			<ol className="m-0 list-none p-0">
				{HOW_IT_WORKS.map((step, index) => (
					<li key={step.title} className="border-b border-line px-4 py-3 last:border-0">
						<p className="text-body font-medium text-ink">
							{index + 1}. {step.title}
						</p>
						<p className="mt-0.5 text-caption text-ink-2">{step.text}</p>
					</li>
				))}
			</ol>
		</Card>
	);
}

/**
 * The page's one primary button: asks the API to start connecting, then sends the whole page to Revolut, which sends it
 * back to `/settings/revolut/callback`. It stays disabled from the press until the page has gone.
 */
function ConnectButton({ label }: { readonly label: string }) {
	const { mutateAsync, isPending, isSuccess, isError, error } = useMutation({ mutationFn: startRevolutConnection });
	const connect = async () => {
		let authorizationUrl: string;
		try {
			({ authorizationUrl } = await mutateAsync());
		} catch {
			// The alert below shows the error.
			return;
		}
		openRevolutApproval(authorizationUrl);
	};
	const leaving = isPending || isSuccess;
	return (
		<div className="flex flex-col items-start gap-2">
			<Button
				variant="primary"
				disabled={leaving}
				onClick={() => {
					void connect();
				}}
			>
				{leaving ? "Opening Revolut…" : label}
			</Button>
			{isError ? (
				<p role="alert" className="text-caption text-alert">
					{error.message}
				</p>
			) : null}
		</div>
	);
}

function ConnectedAccount({ connection }: { readonly connection: MadeConnection }) {
	const refused = connection.status === "reconnect";
	return (
		<Card>
			<CardHeader
				title="Connected account"
				action={refused ? <Pill tone="warn">Needs reconnecting</Pill> : <Pill tone="ok">Active</Pill>}
			/>
			<div className="flex flex-col gap-3 p-4">
				<p className="text-body text-ink-2">
					Connected by {connection.connectedBy.name} on {formatShortDate(connection.connectedAt.slice(0, 10))}
					. Ravnar reads your accounts, their balances and their transactions, and can never move money.
				</p>
				{refused ? (
					<p className="text-caption text-warn">
						Revolut no longer accepts this connection: somebody withdrew Ravnar&apos;s access in Revolut, or
						it expired. Connect again to keep reading your accounts.
					</p>
				) : null}
				<div className="flex flex-wrap items-start gap-2">
					{refused ? <ConnectButton label="Reconnect Revolut" /> : null}
					<DisconnectButton />
				</div>
			</div>
		</Card>
	);
}

const accountColumns: readonly TableColumn<RevolutAccount>[] = [
	{
		key: "account",
		header: "Account",
		cell: account => (
			<span className="block">
				<Link
					to="/settings/revolut"
					search={{ account: account.id }}
					className="block font-medium text-ink hover:underline"
				>
					{account.name}
				</Link>
				<span className="block text-caption text-ink-3">
					{REVOLUT_SUBTYPE_LABELS[account.subtype]} · {account.currency}
				</span>
			</span>
		),
	},
	{ key: "paid-in-by", header: "Paid in by", muted: true, cell: account => revolutPaidInBy(account) ?? "—" },
	{
		key: "balance",
		header: "Balance",
		align: "right",
		cell: account =>
			account.balance === undefined ? "—" : formatAmount(account.balance.amount, account.balance.currency),
	},
];

/**
 * The accounts, read from Revolut when the page opens. Revolut may have stopped honouring the connection since the page
 * last asked; when the accounts say so, the connection is asked again and the page turns to reconnecting.
 */
function RevolutAccounts() {
	const { data, isPending, isError, error, refetch, isRefetching } = useQuery(revolutAccountsQueryOptions());
	const { account: openedId } = Route.useSearch();
	const navigate = Route.useNavigate();
	const queryClient = useQueryClient();
	const status = data?.status;
	useEffect(() => {
		if (status !== undefined && status !== "connected") {
			void queryClient.invalidateQueries({ queryKey: ["settings", "revolut"] });
		}
	}, [status, queryClient]);

	return (
		<Card>
			<CardHeader title="Accounts" />
			{isPending ? (
				<div role="status" aria-label="Loading accounts" className="flex flex-col gap-3 p-4">
					<Skeleton className="h-3.5 w-2/3" />
					<Skeleton className="h-3.5 w-1/2" />
					<Skeleton className="h-3.5 w-3/5" />
				</div>
			) : isError ? (
				<div className="flex flex-col items-start gap-2 p-4">
					<p role="alert" className="text-caption text-alert">
						{error.message}
					</p>
					<Button
						size="sm"
						disabled={isRefetching}
						onClick={() => {
							void refetch();
						}}
					>
						Try again
					</Button>
				</div>
			) : data.status === "connected" ? (
				data.accounts.length === 0 ? (
					<Empty>Revolut shows no accounts for this connection.</Empty>
				) : (
					<Table
						label="Revolut accounts"
						columns={accountColumns}
						rows={data.accounts}
						rowKey={account => account.id}
						interactiveRows
					/>
				)
			) : null}
			<TransactionsDrawer
				account={data?.status === "connected" ? data.accounts.find(entry => entry.id === openedId) : undefined}
				onClose={() => {
					void navigate({ search: previous => ({ ...previous, account: undefined }), replace: true });
				}}
			/>
		</Card>
	);
}

interface TransactionsDrawerProps {
	/** The account whose transactions it shows, or undefined for none: the drawer is then closed. */
	readonly account: RevolutAccount | undefined;
	readonly onClose: () => void;
}

/** One account's latest transactions, on the side, read from Revolut when the drawer opens. */
function TransactionsDrawer({ account, onClose }: TransactionsDrawerProps) {
	return (
		<Drawer
			open={account !== undefined}
			title={account === undefined ? "" : `${account.name} · ${account.currency}`}
			onClose={onClose}
		>
			{account === undefined ? null : <AccountTransactions accountId={account.id} />}
		</Drawer>
	);
}

function AccountTransactions({ accountId }: { readonly accountId: string }) {
	const { data, isPending, isError, error } = useQuery(revolutTransactionsQueryOptions(accountId));
	if (isPending) {
		return (
			<div role="status" aria-label="Loading transactions" className="flex flex-col gap-3">
				<Skeleton className="h-3.5 w-2/3" />
				<Skeleton className="h-3.5 w-1/2" />
				<Skeleton className="h-3.5 w-3/5" />
			</div>
		);
	}
	if (isError) {
		return (
			<p role="alert" className="text-caption text-alert">
				{error.message}
			</p>
		);
	}
	if (data.status !== "connected") {
		return <p className="text-caption text-ink-3">Revolut no longer accepts this connection.</p>;
	}
	if (data.transactions.length === 0) {
		return <p className="text-caption text-ink-3">No transactions on this account lately.</p>;
	}
	return (
		<>
			<p className="mb-3 text-caption text-ink-3">The latest transactions Revolut shows, newest first.</p>
			<ul aria-label="Transactions" className="m-0 list-none p-0">
				{data.transactions.map(transaction => (
					<TransactionRow key={transaction.id} transaction={transaction} />
				))}
			</ul>
		</>
	);
}

/** A transaction as a feed row: what it was, when and with whom, and the amount, negative when money went out. */
function TransactionRow({ transaction }: { readonly transaction: RevolutTransaction }) {
	const { description, counterparty, at, status, amount } = transaction;
	return (
		<li className="flex items-start gap-3 border-b border-line py-2.5 last:border-0">
			<span className="min-w-0 flex-1">
				<span className="block text-body font-medium text-ink">{description}</span>
				<span className="mt-0.5 flex flex-wrap items-center gap-1.5 text-caption text-ink-3">
					{formatShortDate(at.slice(0, 10))}
					{counterparty === undefined || counterparty === description ? null : <> · {counterparty}</>}
					{status === "pending" ? <Pill tone="warn">Pending</Pill> : null}
				</span>
			</span>
			<span className="shrink-0 text-body text-ink tabular-nums">
				{formatAmount(amount.amount, amount.currency)}
			</span>
		</li>
	);
}

function DisconnectButton() {
	const [confirming, setConfirming] = useState(false);
	const queryClient = useQueryClient();
	const askAgain = useAskAgain();
	const { mutateAsync, reset, isPending, isError, error } = useMutation({
		mutationFn: disconnectRevolut,
		// The Revolut page, the settings overview and anything showing the accounts read the connection; the audit entry
		// arrives through a queue a moment after this answer, so it is asked again as it catches up.
		onSuccess: () =>
			Promise.all([
				queryClient.invalidateQueries({ queryKey: ["settings", "revolut"] }),
				queryClient.invalidateQueries({ queryKey: ["revolut"] }),
				askAgain(["settings", "audit-log"]),
			]),
	});
	const disconnect = async () => {
		try {
			await mutateAsync();
		} catch {
			// The dialog shows the error.
			return;
		}
		setConfirming(false);
		showToast("Revolut disconnected.");
	};
	return (
		<>
			<Button
				onClick={() => {
					reset();
					setConfirming(true);
				}}
			>
				Disconnect
			</Button>
			<ConfirmDialog
				open={confirming}
				title="Disconnect Revolut?"
				confirmLabel="Disconnect Revolut"
				busy={isPending}
				error={isError ? error.message : undefined}
				onConfirm={() => {
					void disconnect();
				}}
				onClose={() => {
					setConfirming(false);
				}}
			>
				Ravnar forgets its access to your Revolut accounts and asks Revolut to withdraw it, and Finance can no
				longer send bills there. You can connect again at any time.
			</ConfirmDialog>
		</>
	);
}
